/**
 * Minimal SSE parser built on `fetch` + a ReadableStream reader.
 * We don't use `EventSource` because it can't POST a body and can't set
 * custom headers — both required by the `/chat` endpoint.
 *
 * Parses `data: <json>\n\n` frames, dispatches to the caller, and surfaces
 * abort + network errors as rejected promises so callers can show an
 * error state instead of dangling forever.
 */

import type { ChatEvent, ChatRequest } from '@showpilot/core';
import { ChatRequestError } from './chat-retry';

export interface StreamChatOptions {
  apiBaseUrl: string;
  publicId: string;
  body: ChatRequest;
  onEvent: (event: ChatEvent) => void;
  signal?: AbortSignal;
  /** Draft preview (publish model) — the turn runs on the draft. */
  agentPreviewToken?: string | null;
}

export async function streamChat(opts: StreamChatOptions): Promise<void> {
  const preview = opts.agentPreviewToken
    ? `?preview=${encodeURIComponent(opts.agentPreviewToken)}`
    : '';
  const url = `${opts.apiBaseUrl}/v1/agents/${opts.publicId}/chat${preview}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      accept: 'text/event-stream',
    },
    body: JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (!res.ok || !res.body) {
    throw new ChatRequestError(res.status);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });

      let idx: number;
      while ((idx = buf.indexOf('\n\n')) !== -1) {
        const frame = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const parsed = parseFrame(frame);
        if (parsed) opts.onEvent(parsed);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseFrame(frame: string): ChatEvent | null {
  // SSE frames are lines; we only care about `data:` lines and concatenate
  // them per the spec. `id:` / `event:` / retry are ignored.
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (dataLines.length === 0) return null;
  try {
    const json = JSON.parse(dataLines.join('\n')) as { type?: unknown } | null;
    // Trusted first-party stream — shape gate only (was a zod
    // safeParse). Unknown event types are filtered by the router
    // whitelist downstream, same as before.
    return json && typeof json === 'object' && typeof json.type === 'string'
      ? (json as unknown as ChatEvent)
      : null;
  } catch {
    return null;
  }
}
